import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  TouchedChangeEvent,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  Address,
  ContactName,
  DynamicControlType,
  DynamicForm,
  DynamicFormData,
  DynamicFormValuesSections,
  OptionModel,
  Telephone,
} from '@app/models';
import { requiredFalse, requiredTrue } from '@app/shared/validators';
import { parseStoredDate } from '@src/assets/helpers/dateHelpers';
import { mustNotBeZero, requiredFileList } from '@src/assets/helpers/formValidators';
import { TransformingFormArray, TransformingFormGroup } from '@src/assets/helpers/transformingFormGroup';
import { takeWhile } from 'rxjs';
import { EmrDetails } from '../models/emrDetails.model';
import { AppInfoService } from './app-info.service';

@Injectable({
  providedIn: 'root',
})
export class DynamicFormService {
  constructor(private app: AppInfoService) {}

  public addFormCurrentAnswers(formData: DynamicForm, form: DynamicFormValuesSections): DynamicForm {
    formData.steps.forEach(step => {
      step.sections.forEach(section => {
        const name = section.formName;
        const values = Object.prototype.hasOwnProperty.call(form, name)
          ? form[name]
          : form[name[0].toUpperCase() + name.substring(1)];

        if (values) {
          section.formData.forEach(field => {
            field.currentValue = values[field.controlName];
          });
        }
      });
    });

    return formData;
  }

  /** FormArray is now a TransformingFormArray.
   * When we save the entire FormGroup/FormArray, we can choose to implement a transform function on any of the formControls/formGroups that we want.
   * This allows us to easily change our values before they are saved to the database.
   */
  public buildFormArray(formData: DynamicForm): TransformingFormArray {
    const formArray: TransformingFormArray = new TransformingFormArray([]);

    formData.steps.forEach(step => {
      const stepFormGroup: TransformingFormGroup = new TransformingFormGroup({});

      step.sections.forEach(section => {
        const form: TransformingFormGroup = this.buildFormGroup(section.formData);
        stepFormGroup.addControl(section.formName, form);
      });

      formArray.push(stepFormGroup);
    });

    return formArray;
  }

  public buildFormGroup(formData: DynamicFormData[]): TransformingFormGroup {
    const formGroup = {};
    if (formData[0] == undefined) {
      return new TransformingFormGroup({});
    }

    formData.forEach(control => {
      if (!control.hidden) {
        if (control.controlType !== DynamicControlType.Paragraph && control.controlType !== DynamicControlType.Header) {
          formGroup[control.controlName] = this.buildFormControl(control);
        }
      }
    });

    return new TransformingFormGroup(formGroup);
  }

  public buildFormControl(
    control: DynamicFormData,
    options?: { deferRequiredUntilValidate?: boolean; stepIndex?: number }
  ): AbstractControl {
    switch (control.controlType) {
      case DynamicControlType.Address: {
        const addressValue: Address = control.currentValue != null ? <Address>control.currentValue : null;
        const address = new UntypedFormGroup({
          address1: new UntypedFormControl(
            addressValue != null ? addressValue.address1 : '',
            this.getValidatorsArray(control, options)
          ),
          address2: new UntypedFormControl(addressValue != null ? addressValue.address2 : ''),
          city: new UntypedFormControl(
            addressValue != null ? addressValue.city : '',
            this.getValidatorsArray(control, options)
          ),
          state: new UntypedFormControl(
            addressValue != null ? addressValue.state : '',
            this.getValidatorsArray(control, options)
          ),
          zipCode: new UntypedFormControl(
            addressValue != null ? addressValue.zipCode : '',
            this.getValidatorsArray(control, options)
          ),
        });

        return address;
      }
      case DynamicControlType.ContactName: {
        const contactName: ContactName = control.currentValue != null ? <ContactName>control.currentValue : null;
        const contact = new UntypedFormGroup({
          firstName: new UntypedFormControl(
            contactName != null ? contactName.firstName : '',
            this.getValidatorsArray(control, options)
          ),
          lastName: new UntypedFormControl(
            contactName != null ? contactName.lastName : '',
            this.getValidatorsArray(control, options)
          ),
        });

        return contact;
      }
      case DynamicControlType.Phone: {
        const phoneValue: Telephone = control.currentValue != null ? <Telephone>control.currentValue : null;
        const phone = new UntypedFormGroup({
          phoneNumber: new UntypedFormControl(
            phoneValue != null ? phoneValue.phoneNumber : '',
            this.getValidatorsArray(control, options)
          ),
          extension: new UntypedFormControl(phoneValue != null ? phoneValue.extension : ''),
        });

        return phone;
      }
      case DynamicControlType.Radio: {
        return new UntypedFormControl(control.currentValue, this.getValidatorsArray(control, options));
      }
      case DynamicControlType.Checkbox: {
        const validators = [];
        if (control.validators.required) {
          // Note: Required validator is valid if checkbox is checked.
          validators.push((control: AbstractControl) => {
            const value = control.value;
            if (!value) {
              return { required: true };
            }
            return null;
          });
        }
        if (control.validators.requiredTrue) {
          // Note: RequiredTrue Validator is valid if checkbox value is true.
          validators.push((control: AbstractControl) => {
            const value = control.value;
            if (value !== true) {
              return { requiredTrue: true };
            }
            return null;
          });
        }
        return new FormControl<boolean>(control.currentValue ?? false, validators);
      }
      case DynamicControlType.EmrDetails: {
        const emrDetails: EmrDetails = control.currentValue != null ? <EmrDetails>control.currentValue : null;
        return new UntypedFormGroup({
          currentEmr: new UntypedFormControl(
            emrDetails != null ? emrDetails.currentEmr : '',
            this.getValidatorsArray(control, options)
          ),
          currentEmrYear: new UntypedFormControl(
            emrDetails != null ? emrDetails.currentEmrYear : '',
            this.getValidatorsArray(control, options)
          ),
          currentEmrIntrastate: new UntypedFormControl(emrDetails != null ? emrDetails.currentEmrIntrastate : ''),
          emr1YearAgo: new UntypedFormControl(
            emrDetails != null ? emrDetails.emr1YearAgo : '',
            this.getValidatorsArray(control, options)
          ),
          emr1YearAgoYear: new UntypedFormControl(
            emrDetails != null ? emrDetails.emr1YearAgoYear : '',
            this.getValidatorsArray(control, options)
          ),
          emr1YearAgoIntrastate: new UntypedFormControl(emrDetails != null ? emrDetails.emr1YearAgoIntrastate : ''),
          emr2YearsAgo: new UntypedFormControl(
            emrDetails != null ? emrDetails.emr2YearsAgo : '',
            this.getValidatorsArray(control, options)
          ),
          emr2YearsAgoYear: new UntypedFormControl(
            emrDetails != null ? emrDetails.emr2YearsAgoYear : '',
            this.getValidatorsArray(control, options)
          ),
        });
      }
      case DynamicControlType.OshaIncidenceRates: {
        function buildYearGroup(year: {
          lost_workdays_cases: number;
          no_lost_workdays_cases: number;
          total_cases: number;
          man_hours: number;
          total_rate: number;
          lost_workdays_rate: number;
          no_lost_workdays_rate: number;
        }) {
          return new UntypedFormGroup({
            lost_workdays_cases: new UntypedFormControl(year.lost_workdays_cases ?? 0, [Validators.required]),
            no_lost_workdays_cases: new UntypedFormControl(year.no_lost_workdays_cases ?? 0, [Validators.required]),
            total_cases: new UntypedFormControl(year.total_cases ?? 0),
            man_hours: new UntypedFormControl(year.man_hours ?? 0, [Validators.required, mustNotBeZero]),
            total_rate: new UntypedFormControl(year.total_rate ?? 0),
            lost_workdays_rate: new UntypedFormControl(year.lost_workdays_rate ?? 0),
            no_lost_workdays_rate: new UntypedFormControl(year.no_lost_workdays_rate ?? 0),
          });
        }

        const group = new UntypedFormGroup({
          year_1: buildYearGroup(control.currentValue?.year_1 ?? {}),
          year_2: buildYearGroup(control.currentValue?.year_2 ?? {}),
          year_3: buildYearGroup(control.currentValue?.year_3 ?? {}),
          total_rate: new UntypedFormControl(control.currentValue?.total_rate ?? 0),
          lost_workdays_rate: new UntypedFormControl(control.currentValue?.lost_workdays_rate ?? 0),
          no_lost_workdays_rate: new UntypedFormControl(control.currentValue?.no_lost_workdays_rate ?? 0),
        });

        return group;
      }
      case DynamicControlType.OshaCitationDetails: {
        const group = new UntypedFormGroup({});

        for (let i: number = 0; i < 6; i++) {
          const seriousKey: string = `${i}_serious`;
          const willfulKey: string = `${i}_willful`;
          const repeatKey: string = `${i}_repeat`;
          const unabaitedKey: string = `${i}_unabaited`;
          const totalKey: string = `${i}_total`;

          let seriousVal: number = 0;
          let willfulVal: number = 0;
          let repeatVal: number = 0;
          let unabiatedVal: number = 0;
          let totalVal: number = 0;

          if (control.currentValue) {
            seriousVal = control.currentValue[seriousKey] ?? 0;
            willfulVal = control.currentValue[willfulKey] ?? 0;
            repeatVal = control.currentValue[repeatKey] ?? 0;
            unabiatedVal = control.currentValue[unabaitedKey] ?? 0;
            totalVal = control.currentValue[totalKey] ?? 0;
          }

          group.addControl(seriousKey, new UntypedFormControl(seriousVal, []));
          group.addControl(willfulKey, new UntypedFormControl(willfulVal, []));
          group.addControl(repeatKey, new UntypedFormControl(repeatVal, []));
          group.addControl(unabaitedKey, new UntypedFormControl(unabiatedVal, []));
          group.addControl(totalKey, new UntypedFormControl(totalVal, []));
        }

        return group;
      }
      case DynamicControlType.LausdCertificationSection: {
        // Stamp dateOfExecution when the form is serialized, same as the signature control.
        const group = new TransformingFormGroup({}, value => ({
          ...value,
          dateOfExecution: new Date().toISOString(),
        }));

        group.addControl(
          'stateOfExecution',
          new UntypedFormControl(control.currentValue?.stateOfExecution ?? 'California', [Validators.required])
        );
        group.addControl(
          'stateOfExecutionReadonly',
          new UntypedFormControl({ value: control.currentValue?.stateOfExecution ?? 'California', disabled: true }, [])
        );
        group.addControl(
          'countyOfExecution',
          new UntypedFormControl(control.currentValue?.countyOfExecution ?? '', [Validators.required])
        );
        group.addControl(
          'signerPosition',
          new UntypedFormControl(control.currentValue?.signerPosition ?? '', [Validators.required])
        );
        group.addControl('otherPosition', new UntypedFormControl(control.currentValue?.otherPosition ?? '', []));
        group.addControl(
          'firmName',
          new UntypedFormControl(control.currentValue?.firmName ?? '', [Validators.required])
        );
        group.addControl(
          'dateOfExecution',
          new UntypedFormControl(control.currentValue?.dateOfExecution ?? new Date().toLocaleDateString(), [
            Validators.required,
          ])
        );
        // check immediately to inform user if the date needs to be updated
        group.get('dateOfExecution').markAsTouched();
        group.addControl(
          'placeOfExecution',
          new UntypedFormControl(control.currentValue?.placeOfExecution ?? '', [Validators.required])
        );
        group.addControl(
          'signerName',
          new UntypedFormControl(control.currentValue?.signerName ?? '', [Validators.required])
        );
        group.addControl(
          'digitalSignature',
          new UntypedFormControl(control.currentValue?.digitalSignature ?? false, [Validators.required])
        );

        return group;
      }
      case DynamicControlType.RevenueInformation: {
        const group = new UntypedFormGroup({});

        group.addControl(
          'revenueYear',
          new UntypedFormControl(control.currentValue?.revenueYear ?? '', [Validators.required])
        );
        group.addControl(
          'grossRevenue',
          new UntypedFormControl(control.currentValue?.grossRevenue ?? '', [Validators.required])
        );
        group.addControl(
          'constructionRevenue',
          new UntypedFormControl(control.currentValue?.constructionRevenue ?? '', [Validators.required])
        );
        group.addControl(
          'contractsCount',
          new UntypedFormControl(control.currentValue?.contractsCount ?? '', [Validators.required])
        );
        return group;
      }
      case DynamicControlType.Helper: {
        const helper = new UntypedFormControl(control.currentValue);
        helper.disable();
        return helper;
      }
      case DynamicControlType.Score: {
        const score = new UntypedFormControl(control.currentValue ?? '');
        score.disable();
        return score;
      }
      case DynamicControlType.Currency:
      case DynamicControlType.CurrencyWhole: {
        return new FormControl<number>(control.currentValue ?? 0, this.getValidatorsArray(control, options));
      }
      case DynamicControlType.AgencySelect: {
        const agencySelect = new FormArray([]);
        const valsArray = this.getValidatorsArray(control, options);
        setTimeout(() => {
          // Deals with 1 (one) ExpressionChangedAfterChecked error.
          // Probably introduced 1000 new bugs. yay.
          agencySelect.addValidators(valsArray);
        });
        return agencySelect;
      }
      case DynamicControlType.DirNumber: {
        return new UntypedFormGroup(
          {
            number: new FormControl<string>(control.currentValue?.number ?? '', [
              Validators.required,
              Validators.minLength(10),
              Validators.maxLength(10),
            ]),
            year: new FormControl<number>(control.currentValue?.year ?? null, [
              Validators.required,
              Validators.min(1900),
              Validators.max(2100),
            ]),
          },
          this.getValidatorsArray(control, options)
        );
      }
      case DynamicControlType.FinancialCapacity: {
        return new UntypedFormGroup(
          {
            year: new FormControl<string | null>(control.currentValue?.year ?? null, [
              Validators.required,
              Validators.min(1900),
              Validators.max(2100),
            ]),
            revenue: new FormControl<number | null>(control.currentValue?.revenue ?? null, [Validators.required]),
          },
          this.getValidatorsArray(control, options)
        );
      }
      case DynamicControlType.CertificationsSelect:
      case DynamicControlType.ClassificationsSelect: {
        return new FormControl<OptionModel<string>[]>(control.currentValue, this.getValidatorsArray(control, options));
      }
      case DynamicControlType.LausdProjects:
      case DynamicControlType.Projects: {
        const projectArray = new FormArray([]);

        (control.currentValue ??= []).forEach((project: { id: number; valid: boolean; isPublicWorks: boolean }) => {
          projectArray.push(
            new FormGroup({
              id: new FormControl<number>(project.id),
              valid: new FormControl<boolean>(project.valid ?? false, requiredTrue),
              isPublicWorks: new FormControl<boolean>(project.isPublicWorks ?? false),
            })
          );
        });
        // Commenting for later use - will cleanup after this change has been completed & tested
        // https://linear.app/colbi/issue/QUA-1028/qb2-re-add-logic-for-step-bubble-colors

        // if (control.validators?.required) {
        //   const shouldDefer = options?.deferRequiredUntilValidate;
        //   const stepIndex = options?.stepIndex;
        //   projectArray.addValidators((arrayControl: AbstractControl) => {
        //     if (shouldDefer && !this.app.isStepValidating(stepIndex)) {
        //       return null;
        //     }

        //     const groups = (arrayControl as FormArray).controls as FormGroup[];
        //     const validProjects = groups.filter(g => g.controls.valid.value === true);

        //     const requiredCount = control.validators?.minlength ?? 4;
        //     if (validProjects.length < requiredCount) {
        //       return { required: `You must submit at least ${requiredCount} fully completed projects.` };
        //     }

        //     const publicWorksCount = validProjects.filter(g => g.controls.isPublicWorks.value === true).length;
        //     const requiredPublicWorksCount = control.validators?.minAmount ?? 2;
        //     if (publicWorksCount < requiredPublicWorksCount) {
        //       return {
        //         publicWorks: `At least ${requiredPublicWorksCount} of your submitted projects must be public works projects.`,
        //       };
        //     }

        //     return null;
        //   });
        //   projectArray.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        // }

        return projectArray;
      }
      case DynamicControlType.InputGrid: {
        const required = control.validators?.required ?? false;
        const gridArray = new TransformingFormArray([], (val: any[]) => {
          /**
           * Using TransformingFormArray so that when we save the entire FormGroup, we can transform the value of our inputGrid
           * Whenever we save the inputGrid, we want to filter out any rows that have all empty values.
           */
          if (val.length == 1) return val;

          const reducedRows = val.reduce((accumulator, currentRow) => {
            if (
              !Object.values(currentRow).every(
                item => item == undefined || item == null || (typeof item == 'string' && item.trim() == '')
              )
            ) {
              accumulator.push(currentRow);
            }
            return accumulator;
          }, []);

          // In case we've saved an InputGrid with multiple rows that have nothing in them, save 1 empty row instead.
          if (reducedRows.length == 0) {
            reducedRows.push(val[0]);
          }
          return reducedRows;
        });

        // Make sure the entire grid (at least the first row) is touched when the user touches the grid.
        gridArray.events.pipe(takeWhile(val => !(val instanceof TouchedChangeEvent))).subscribe({
          complete: () => {
            gridArray.markAllAsTouched();
          },
        });

        if (!control.currentValue) {
          // If there is no value, create 1 empty object of inputs for the grid.
          const currentValue = {};
          control.inputGridFields.forEach(field => {
            currentValue[field.controlName] = undefined;
          });
          control.currentValue = [currentValue];
        }
        control.currentValue.forEach((row: { [property: string]: string }) => {
          const rowGroup = new FormGroup({});

          /** Add an object reference so we can access the DynamicFormData for each input in O(1) time instead of looping. */
          const controlReference: { [property: string]: DynamicFormData } = {};
          control.inputGridFields.forEach((field: DynamicFormData) => {
            controlReference[field.controlName] = field;
          });

          Object.keys(row).forEach((key: string) => {
            // Adding this as untypedFormControl because we potentially have date entries.
            // Date fields in the grid (e.g. CSLB classification expiration) must load as local calendar dates.
            const fieldMeta = controlReference[key];
            const raw = row[key];
            const value =
              fieldMeta?.controlType === DynamicControlType.Date && raw != null && raw !== ''
                ? parseStoredDate(raw)
                : raw;
            rowGroup.addControl(key, new UntypedFormControl(value));
          });

          gridArray.push(rowGroup);
        });
        if (required) {
          // If this is required, we need to check that at least 1 row has a value in it. If not, this gridArray is invalid.
          gridArray.addValidators((arrayControl: AbstractControl) => {
            for (const row of arrayControl.value) {
              for (const key in row) {
                if (
                  row[key] !== undefined &&
                  row[key] !== null &&
                  (typeof row[key] !== 'string' || row[key].trim() !== '')
                ) {
                  const gridRowGroup = gridArray.controls[0] as FormGroup;
                  for (const gridRowControl of Object.values(gridRowGroup.controls)) {
                    gridRowControl.setErrors(null);
                  }
                  return null;
                }
              }
            }
            const gridRowGroup = gridArray.controls[0] as FormGroup;
            for (const gridRowControl of Object.values(gridRowGroup.controls)) {
              gridRowControl.setErrors({ required: true });
            }
            // if (gridArray.touched) {
            //   gridRowGroup.markAllAsTouched();
            // }
            return { required: true };
          });
        }
        return gridArray;
      }
      case DynamicControlType.Signature: {
        // Stamp the signature date when the form is serialized. The control may have been
        // filled in on an earlier day; save and submit both read getTransformedValue().
        return new TransformingFormGroup(
          {
            fullName: new FormControl<string>(
              control.currentValue?.fullName ?? '',
              this.getValidatorsArray(control, options)
            ),
            date: new UntypedFormControl(control.currentValue?.date ?? '', this.getValidatorsArray(control, options)),
            location: new FormControl<string>(
              control.currentValue?.location ?? '',
              this.getValidatorsArray(control, options)
            ),
          },
          value => ({
            ...value,
            date: new Date().toISOString(),
          })
        );
      }
      case DynamicControlType.Date: {
        // Calendar-only fields (e.g. CSLB expiration): load as local date, never UTC timestamps.
        return new UntypedFormControl(
          control.currentValue != null && control.currentValue !== '' ? parseStoredDate(control.currentValue) : null,
          this.getValidatorsArray(control, options)
        );
      }
      case DynamicControlType.Input: {
        // Force input to be a string.
        // Why are we doing this, especially when we could have "number", "year", or "Percentage" inputs?
        // The name of the game here is consistency, when this answer gets passed back to the validation parser in the back end.
        // If things are not consistent then we may see some unexpected parsing errors.
        // If the answer parser on the back end is expecting a number, it will parse this value into a number.
        let val: string = control.currentValue ?? '';
        if (typeof val != 'string') {
          val = val + '';
        }
        return new UntypedFormControl(val ?? '', this.getValidatorsArray(control, options));
      }
      default: {
        return new UntypedFormControl(control.currentValue ?? null, this.getValidatorsArray(control, options));
      }
    }
  }

  public getValidatorsArray(
    data: DynamicFormData,
    options?: { deferRequiredUntilValidate?: boolean; stepIndex?: number }
  ): ValidatorFn[] {
    if (!data.validators || !this.app.status.isInVendorCourt()) {
      return [];
    }
    const retValue: ValidatorFn[] = [];
    if (data.validators.required) {
      // No files yet looks like an empty list, so we use a check that treats that as incomplete.
      const requiredFn = data.controlType === DynamicControlType.FileUpload ? requiredFileList : Validators.required;
      if (options?.deferRequiredUntilValidate) {
        const stepIndex = options.stepIndex;
        retValue.push(ctrl => (this.app.isStepValidating(stepIndex) ? requiredFn(ctrl) : null));
      } else {
        retValue.push(requiredFn);
      }
    }
    if (data.validators.minAmount) {
      retValue.push(Validators.min(data.validators.minAmount));
    }
    if (data.validators.maxAmount) {
      retValue.push(Validators.max(data.validators.maxAmount));
    }
    if (data.validators.requiredFalse) {
      retValue.push(requiredFalse);
    }
    if (data.validators.requiredTrue) {
      retValue.push(requiredTrue);
    }
    if (data.validators.minlength >= 0) {
      retValue.push(Validators.minLength(data.validators.minlength));
    }
    if (data.validators.maxlength >= 0) {
      retValue.push(Validators.maxLength(data.validators.maxlength));
    }
    return retValue;
  }
}
